import { useState } from "react";
import { api } from "../api/client";
import { deriveSpecies, isManuallyTagged, type Shot } from "../api/types";
import { LikeCommentBar } from "./LikeCommentBar";
import { Lightbox } from "./Lightbox";
import { Moon, moonPhase } from "./Moon";
import { fmtDate, fmtTime, manualTagTooltip, SPECIES_LABEL } from "./format";

interface Props {
  shot: Shot;
  funky?: boolean;
  showNewBadge?: boolean;
  isNew?: boolean;
  focused?: boolean;
  onChanged?: (shot: Shot) => void;
  onHidden?: (imageId: string) => void;
  /**
   * When provided, the card delegates opening the lightbox to the parent
   * (so a single screen-level Lightbox can manage prev/next/day navigation).
   * Without it, the card hosts its own internal Lightbox — keeps standalone
   * uses (tests, hero rendering) working unchanged.
   */
  onOpen?: (shot: Shot) => void;
}

export function ImageCard({
  shot,
  funky,
  showNewBadge,
  isNew,
  focused,
  onChanged,
  onHidden,
  onOpen,
}: Props) {
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);
  const hostsLightbox = !onOpen;

  const captured = new Date(shot.capturedAt);
  const species = deriveSpecies(shot.analysis);
  const tagClass = species ?? "untagged";
  const tagLabel = species ? SPECIES_LABEL[species] : "Untagged";
  const edited = isManuallyTagged(shot.analysis);

  const src = errored ? "" : api.imageContentUrl(shot.imageId);

  return (
    <article
      id={`shot-${shot.imageId}`}
      className={`shot${focused ? " shot-focus" : ""}`}
      data-funky={funky ? "1" : "0"}
    >
      <div
        className="shot-media"
        onClick={() => {
          if (errored) return;
          if (onOpen) onOpen(shot);
          else setOpen(true);
        }}
      >
        {src && (
          <img
            className="photo"
            src={src}
            alt={`${shot.cameraId} ${fmtDate(captured)} ${fmtTime(captured)}`}
            loading="lazy"
            onError={() => setErrored(true)}
          />
        )}

        <div className="shot-badge">
          <span className="dot" />
          {shot.cameraId}
        </div>

        <span
          className={`shot-tag ${tagClass}${edited ? " edited" : ""}`}
          title={edited ? manualTagTooltip(shot.analysis) : "AI species tag"}
        >
          {tagLabel}
          {edited && <PencilIcon />}
        </span>

        {showNewBadge && isNew && <span className="shot-new">● New</span>}

        <div className="shot-strip">
          <span className="cam">{shot.cameraId.toUpperCase()}</span>
          <span className="sep">·</span>
          <Moon phase={moonPhase(captured)} />
          <span className="sep">·</span>
          <span>{fmtDate(captured)}</span>
          <span>{fmtTime(captured)}</span>
        </div>
      </div>

      <LikeCommentBar
        imageId={shot.imageId}
        variant="compact"
        analysis={shot.analysis}
        onChanged={onChanged}
      />

      {hostsLightbox && open && (
        <Lightbox
          shot={shot}
          onClose={() => setOpen(false)}
          onHidden={onHidden}
          onChanged={onChanged}
        />
      )}
    </article>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
